import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {ActionMenuInfoTile} from '#/components/actionmenu/action-menu-info-tile';
import {ActionMenuLayoutContentNamed, ActionMenuLayoutFooter} from '#/components/actionmenu/action-menu-layout';
import {actionMenuMessageContent, useMessageCasesToContent} from '#/components/actionmenu/action-menu-message-content';
import {ActionMenuOverviewInfoRow} from '#/components/actionmenu/action-menu-overview';
import {ActionMenuContentSectionOverview} from '#/components/actionmenu/content/action-menu-content-section-overview';
import {TransactionVariables} from '#/components/actionmenu/sections/transaction/transaction-flow-data';
import {ActionMenuFormContainer, ActionMenuTransactionReviewButton} from '#/components/actionmenu/subcomponents';
import {ActionMenuTokenInput} from '#/components/actionmenu/token-input/action-menu-token-input';
import {useActionMenuTokenInputData} from '#/components/actionmenu/token-input/use-action-menu-token-input-data';
import {ActionMenuSection, MessageCase} from '#/components/actionmenu/types';
import {useStableTokenBalance} from '#/hooks/query/use-stable-token-balance';
import {useDismissableInfoTile} from '#/hooks/use-dismisable-info-tile';
import {useQueryNavigationActions} from '#/hooks/use-query-navigation-actions';
import {TradeTab} from '#/navigation/tabs';
import {TokenType} from '#/repository/types';
import {getMinimumTransactionFees} from '#/utils/get-transaction-fees';
import {DefaultChildren} from '#/utils/utils';
import {
  TransactionType,
  necessaryAmountForLandTokenRent,
  receivedLiquidityTokenForLiquidityProvided,
} from '@shareland/shareland-common';
import React, {FunctionComponent, useContext} from 'react';
import {useTranslation} from 'react-i18next';

const transactionType = TransactionType.MintLiquidityToken;
type TransactionVars = TransactionVariables[typeof transactionType];

export const ActionMenuRentRentTab: FunctionComponent<DefaultChildren> = () => {
  // Context
  const {landMarketInfo, landAccountInfo, initiateTransaction} = useContext(ActionMenuContext);

  // External hooks
  const [showInfoTile, onCloseTile] = useDismissableInfoTile(transactionType);

  // Internal hooks
  const actionMenuTokenInputData = useActionMenuTokenInputData({
    transactionType,
    initialSelectedKey: TokenType.LAND,
  });

  // Vars
  const {landTokenAmount, stableTokenAmount} = actionMenuTokenInputData.tokenAmounts;
  const viewOnly = landAccountInfo.landTokenAmount === 0;
  const lpTokenAmount = receivedLiquidityTokenForLiquidityProvided(landTokenAmount, stableTokenAmount, landMarketInfo);
  const transactionFee = getMinimumTransactionFees(stableTokenAmount);
  const rentStableAmount = necessaryAmountForLandTokenRent(landMarketInfo.marketPrice, landTokenAmount);
  const rentStableAmountWithFee = rentStableAmount + transactionFee;

  const transactionVars: TransactionVars = {
    landTokenAmount,
    stableTokenAmount,
    transactionFee,
    rentStableAmount,
    rentStableAmountWithFee,
    lpTokenAmount,
  };

  const noticeMessages = useTokenInputMessage(transactionVars, actionMenuTokenInputData);
  const canStartTransactionFlow = stableTokenAmount !== 0 && noticeMessages.every(elem => elem.canSubmitForm);

  // Functions
  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!canStartTransactionFlow) return;

    initiateTransaction({
      type: transactionType,
      data: transactionVars,
    });
  };

  return (
    <ActionMenuFormContainer onSubmit={onSubmit}>
      <ActionMenuLayoutContentNamed noticeMessages={noticeMessages}>
        {{
          info: showInfoTile && <ActionMenuInfoTile onClose={onCloseTile} tileKey={transactionType} />,
          grow: (
            <ActionMenuTokenInput
              tokenInputData={actionMenuTokenInputData}
              maxButtonLandAmount={landAccountInfo.landTokenAmount}
              viewOnly={viewOnly}
            />
          ),
          overview: (
            <ActionMenuContentSectionOverview className={viewOnly ? 'view-only' : ''}>
              <ActionMenuOverviewInfoRow.RentLand landTokenAmount={transactionVars.landTokenAmount} />
              <ActionMenuOverviewInfoRow.RentStable stableTokenAmount={transactionVars.rentStableAmount} />
              <ActionMenuOverviewInfoRow.TransactionFee transactionFee={transactionVars.transactionFee} />
              <ActionMenuOverviewInfoRow.TotalLandStable
                landTokenAmount={transactionVars.landTokenAmount}
                stableTokenAmount={transactionVars.rentStableAmountWithFee}
              />
              <ActionMenuOverviewInfoRow.RentReceive lpTokenAmount={transactionVars.lpTokenAmount} />
            </ActionMenuContentSectionOverview>
          ),
        }}
      </ActionMenuLayoutContentNamed>

      <ActionMenuLayoutFooter>
        <ActionMenuTransactionReviewButton
          className={viewOnly ? 'view-only' : ''}
          disabled={!canStartTransactionFlow}
        />
      </ActionMenuLayoutFooter>
    </ActionMenuFormContainer>
  );
};

const useTokenInputMessage = (
  transactionVars: TransactionVars,
  actionMenuTokenInputData: ReturnType<typeof useActionMenuTokenInputData>,
) => {
  const {land, landAccountInfo} = useContext(ActionMenuContext);
  const {t} = useTranslation('translation');
  const {data: stableTokenBalance} = useStableTokenBalance();
  const {
    actionMenu: {openTrade},
  } = useQueryNavigationActions();

  const cases: Record<string, MessageCase> = {
    noLandTokenToSell: {
      condition: () => landAccountInfo.landTokenAmount === 0,
      message: () =>
        actionMenuMessageContent.needToBuyOrBorrowToRent(t, land.tokenSymbol, () => {
          openTrade(undefined, TradeTab.BUY, ActionMenuSection.INFORMATION);
        }),
    },
    notEnoughNativeToken: {
      condition: () => transactionVars.landTokenAmount > landAccountInfo.landTokenAmount,
      message: () => actionMenuMessageContent.notEnoughTokenAmount(t, land.tokenSymbol),
    },
    notEnoughStableToken: {
      condition: () => transactionVars.stableTokenAmount + transactionVars.transactionFee > Number(stableTokenBalance),
      message: () => actionMenuMessageContent.notEnoughUsdc(t),
    },
  };

  return useMessageCasesToContent(cases, {
    inputValidationMessage: actionMenuTokenInputData.tokenInput.validationMessage,
  });
};
